<script setup lang="ts">
import { computed } from "vue";

interface FooterSection {
    title: string;
    links: Array<{
        label: string;
        path: string;
    }>;
}

// Determine if we're on the test site, live site, or localhost
const baseUrl = computed(() => {
    const { hostname } = window.location;
    // Use test URLs for both test.mytrainticket.co.uk and localhost
    if (hostname.includes("test.mytrainticket.co.uk") || hostname === "localhost" || hostname === "127.0.0.1") {
        return "https://test.mytrainticket.co.uk";
    }
    return "https://mytrainticket.co.uk";
});

// Define footer sections with relative paths instead of full URLs
const footerSections: FooterSection[] = [
    {
        title: "Help and support",
        links: [
            { label: "Contact Us", path: "/contact-us/" },
            { label: "FAQs", path: "/faqs" },
            { label: "About MyTrainTicket", path: "/about-mytrainticket" },
            { label: "Help and Support", path: "/help-and-support/" },
            { label: "Advertise with us", path: "/advertise-with-us" },
            { label: "Business Accounts", path: "/business/" },
        ],
    },
    {
        title: "Terms and conditions",
        links: [
            {
                label: "Terms and Conditions",
                path: "/mytrainticket-terms-and-conditions/",
            },
            { label: "Privacy Policy", path: "/privacy-policy/" },
            {
                label: "National Rail Conditions of Travel",
                path: "https://www.nationalrail.co.uk/times_fares/conditions-of-travel.aspx",
            },
        ],
    },
    {
        title: "Partners and affiliates",
        links: [
            // { label: "Diners Club International", path: "/diners-club-international/" },
            { label: "RailEurope", path: "/european-rail-travel/" },
            { label: "Eurostar", path: "/eurostar/" },
        ],
    },
];

// Function to get the full URL for a path
const getFullUrl = (path: string) => {
    // If already a fully qualified URL, don't add the base URL
    if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
    }
    return `${baseUrl.value}${path}`;
};
</script>

<template>
    <footer>
        <div class="footer-container">
            <div v-for="(section, index) in footerSections" :key="index" class="footer-column">
                <h3>{{ section.title }}</h3>
                <div class="footer-links">
                    <a v-for="(link, linkIndex) in section.links" :key="linkIndex" :href="getFullUrl(link.path)" target="_blank">
                        {{ link.label }}
                    </a>
                </div>
            </div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
footer {
    background-color: #f8f8f8;
    padding: 20px 0;
    border-top: 1px solid #e6e6e6;
}

.footer-container {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.footer-column {
    flex: 1;
    padding: 0 15px;

    h3 {
        color: #333;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 15px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e6e6e6;
    }
}

.footer-links {
    display: flex;
    flex-direction: column;
    gap: 8px;

    a {
        color: #666;
        text-decoration: none;
        font-size: 14px;
        line-height: 1.6;
        transition: color 0.2s ease;

        &:hover {
            color: #333;
            text-decoration: underline;
        }
    }
}

@media (max-width: 768px) {
    .footer-container {
        flex-direction: column;
        gap: 20px;
    }

    .footer-column {
        padding: 0;
    }
}
</style>
